import { FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
  type XAxisTickContentProps,
} from 'recharts';
import type { VendasForecast } from '@/types/dashboards';
import { fmtMedida, type Medida, type PontoMensal } from '@/utils/equiplexVendas';
import { fmtData, fmtPct, mesCurto } from '@/utils/painelFormat';
import { escalaDoGrafico, type Grandeza, margemDoRotuloEmPe, type MedidasRotulo } from '@/utils/rotulosGrafico';
import AreaDoGrafico from '../AreaDoGrafico';
import { ALTURA_GRAFICO, FONTE_ROTULO, FONTE_EIXO, tickEixo, type CoresPainel } from '../chartTheme';
import Legenda from '../Legenda';
import PainelCard from '../PainelCard';
import { rotuloNoTopo, type LinhaExtra } from '../rotuloBarra';
import TooltipBox, { type LinhaTooltip } from '../TooltipBox';

interface Props {
  serie: PontoMensal[];
  medida: Medida;
  mesSelecionado: string;
  forecast: VendasForecast;
  corte: string;
  cores: CoresPainel;
}

interface Barra extends PontoMensal {
  rotulo: string;
}

const nfPct0 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

/** "% do forecast" numa segunda linha, em cima do número do faturado — só onde há forecast para comparar. */
const pctAcima = (barras: Barra[], cor: string): LinhaExtra => ({
  texto: (i) => {
    const pct = barras[i]?.pctDoForecast;
    return pct === null || pct === undefined ? null : `${nfPct0.format(pct)}%`;
  },
  cor,
});

/** "R$ milhões", "milhões de unidades", "mil unidades": a grandeza dos números das barras. */
function grandezaDaMedida(grandeza: Grandeza, medida: Medida): string {
  if (medida === 'valor') return grandeza ? `R$ ${grandeza}` : 'R$';
  if (grandeza === 'milhões') return 'milhões de unidades';
  return grandeza === 'mil' ? 'mil unidades' : 'unidades';
}

function linhasTooltip(b: Barra, medida: Medida, corte: string, cores: CoresPainel): LinhaTooltip[] {
  const linhas: LinhaTooltip[] = [
    { rotulo: 'Forecast', valor: b.forecast === null ? 'sem forecast' : fmtMedida(b.forecast, medida, false), cor: cores.programado },
  ];
  if (b.faturado !== null) linhas.push({ rotulo: 'Faturado', valor: fmtMedida(b.faturado, medida, false), cor: cores.faturado });
  if (b.pctDoForecast !== null) linhas.push({ rotulo: '% do forecast', valor: fmtPct(b.pctDoForecast) });
  if (b.esperado !== null) {
    linhas.push({ rotulo: `Esperado até ${fmtData(corte).slice(0, 5)}`, valor: fmtMedida(b.esperado, medida, false) });
  }
  return linhas;
}

function subtitulo(forecast: VendasForecast): string {
  const base = 'Mês de produção (16 a 15). Faturado = notas válidas de produto acabado.';
  if (!forecast.importado) return `${base} Forecast = demanda do Balanceamento de Linha.`;
  return `${base} Forecast importado no Balanceamento em ${fmtData(forecast.importado.aplicado_em)}.`;
}

/** Duas colunas por mês (forecast e faturado): deitado, ~74 px por mês; em pé, ~44 (03/11/2026). */
const MEDIDAS: MedidasRotulo = { reservado: 112, deitado: 74, emPe: 44 };

/**
 * Forecast × faturado mês a mês: os meses vividos mostram como o forecast se saiu; os
 * futuros, o que o comercial espera. O mês escolhido fica com o rótulo do eixo em destaque.
 */
const ForecastFaturadoChart: FC<Props> = ({ serie, medida, mesSelecionado, forecast, corte, cores }) => {
  const barras: Barra[] = serie.map((p) => ({ ...p, rotulo: mesCurto(p.label) }));
  // Dois números por mês, até 12 meses: na grandeza do maior valor, sem sufixo, para caber na barra.
  const escala = escalaDoGrafico(barras.flatMap((b) => [b.forecast ?? 0, b.faturado ?? 0]));
  const extra = pctAcima(barras, cores.textoMuted);
  const topoEmPe = margemDoRotuloEmPe(
    barras.flatMap((b, i) => [escala.formatar(b.forecast ?? 0), `${escala.formatar(b.faturado ?? 0)}  ${extra.texto(i) ?? ''}`]),
    FONTE_ROTULO,
  );
  const selecionado = barras.find((b) => b.key === mesSelecionado)?.rotulo;

  const conteudoTooltip = ({ active, payload }: TooltipContentProps) => {
    const b = active && payload?.[0] ? (payload[0].payload as Barra) : null;
    if (!b) return null;
    const rodape = b.emAndamento ? 'Mês aberto: o faturado ainda vai crescer.' : b.futuro ? 'Mês futuro.' : undefined;
    return <TooltipBox titulo={b.label} linhas={linhasTooltip(b, medida, corte, cores)} rodape={rodape} />;
  };

  // Mês em cima, ano embaixo: com 8 a 12 meses no eixo, "Out/26" numa linha só encavala no celular.
  const tick = ({ x, y, payload }: XAxisTickContentProps) => {
    const valor = String(payload?.value ?? '');
    const [nome, ano] = valor.split('/');
    const ativo = valor === selecionado;
    return (
      <text
        x={Number(x)}
        y={Number(y) + 12}
        textAnchor="middle"
        fontSize={FONTE_EIXO}
        fontWeight={ativo ? 700 : 400}
        fill={ativo ? cores.texto : cores.textoMuted}
      >
        <tspan x={Number(x)}>{nome}</tspan>
        {ano && (
          <tspan x={Number(x)} dy={14}>
            {ano}
          </tspan>
        )}
      </text>
    );
  };

  return (
    <PainelCard testId="vendas-chart-mensal" titulo="Forecast × faturado por mês" subtitulo={subtitulo(forecast)}>
      <Legenda
        itens={[
          { rotulo: 'Forecast (Balanceamento)', cor: cores.programado },
          { rotulo: 'Faturado (nota fiscal)', cor: cores.faturado },
        ]}
      />
      <p className="text-xs text-content-muted" data-testid="vendas-chart-mensal-unidade">
        Números nas barras em {grandezaDaMedida(escala.grandeza, medida)}; o % em cima do faturado é quanto do forecast
        faturou.
      </p>
      <AreaDoGrafico altura={ALTURA_GRAFICO} categorias={barras.length} medidas={MEDIDAS}>
        {(modo) => (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barras} margin={{ top: modo === 'em-pe' ? Math.max(topoEmPe, 40) : 40, right: 8, bottom: 0, left: 8 }} barGap={4} barCategoryGap="16%">
              <CartesianGrid vertical={false} stroke={cores.grade} />
              <XAxis dataKey="rotulo" tick={tick} height={44} axisLine={{ stroke: cores.eixo }} tickLine={false} interval={0} />
              <YAxis
                tickFormatter={(v: number) => fmtMedida(v, medida)}
                tick={tickEixo(cores)}
                axisLine={false}
                tickLine={false}
                width={96}
              />
              <Tooltip content={conteudoTooltip} cursor={{ fill: cores.grade, fillOpacity: 0.35 }} />
              <Bar dataKey="forecast" name="Forecast" fill={cores.programado} radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false}>
                {modo && <LabelList dataKey="forecast" content={rotuloNoTopo(escala.formatar, cores.texto, undefined, modo === 'em-pe')} />}
              </Bar>
              <Bar dataKey="faturado" name="Faturado" fill={cores.faturado} radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false}>
                {modo && (
                  <LabelList dataKey="faturado" content={rotuloNoTopo(escala.formatar, cores.texto, extra, modo === 'em-pe')} />
                )}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </AreaDoGrafico>
    </PainelCard>
  );
};

export default ForecastFaturadoChart;
